/** PostHog loads lazily after first paint — analytics must never sit in the
 * critical bundle. Events fired before the SDK arrives are queued and
 * replayed on init. Surveys are disabled: the extension script is dead
 * weight here and its endpoint isn't CORS-allowed for this origin.
 *
 * GDPR gate: the SDK loads only under granted analytics consent
 * (lib/consent.ts — own banner record, or inherited from the main site's
 * `.lingvanex.com` tarteaucitron cookie). Until the user decides, events
 * queue in memory and nothing is transmitted or stored; declining drops the
 * queue, withdrawing later opts out and purges PostHog's storage.
 *
 * Every event carries the Lingvanex-standard property set (Page Name,
 * Product, Platform, Locale, utm_*, …) — merged into each capture AND
 * registered as super properties so $pageview/$pageleave get them too.
 * Language properties follow the company convention: English names plus
 * short codes ("Language First": "English", "Language First Code": "en"). */
import { UI_LOCALES, detectLocale } from '../i18n/config'
import { sttEnglishName, sttShortCode } from '../lib/languages'
import { analyticsConsent, onConsentChange } from '../lib/consent'

type PostHog = typeof import('posthog-js').default

const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const apiHost = import.meta.env.VITE_POSTHOG_HOST as string | undefined

let client: PostHog | null = null
let queue: Array<(p: PostHog) => void> = []
let uiLanguage: string | null = null

// utm_* captured once per session — the app cleans its URL on navigation, so
// the landing values are kept for the rest of the visit. Read into memory
// here (no storage touched); persisted to sessionStorage only after consent.
const UTM_STORAGE_KEY = 'stt_utm'
const utm: { source?: string; medium?: string; campaign?: string } = (() => {
  try {
    const q = new URLSearchParams(window.location.search)
    const fresh = {
      source: q.get('utm_source') ?? undefined,
      medium: q.get('utm_medium') ?? undefined,
      campaign: q.get('utm_campaign') ?? undefined,
    }
    const stored = JSON.parse(sessionStorage.getItem(UTM_STORAGE_KEY) || '{}')
    // Our own landing tag never displaces an external source already known
    // for this session: lingvanex.com → app → footer "Tools" → static page →
    // CTA back would otherwise re-attribute the main site's visitor to SEO
    if (fresh.source === 'stt_seo' && stored.source && stored.source !== 'stt_seo') return stored
    if (fresh.source || fresh.medium || fresh.campaign) return fresh
    return stored
  } catch {
    return {}
  }
})()

const PRODUCT_PROPS = {
  'Page Name': 'Speech-to-Text Transcription',
  'Page Title': 'Speech-to-Text Transcription',
  Source: 'Speech-to-Text Transcription Lingvanex.com',
  Product: 'Speech-to-Text Transcription',
  Platform: 'Lingvanex.com',
  Position: '',
}

function localeProps() {
  const code = uiLanguage ?? detectLocale()
  return {
    'Locale Code': code,
    'Locale Name': UI_LOCALES.find((l) => l.code === code)?.english ?? 'English',
  }
}

/** The standard property set present on every event. */
function standardProps(): Record<string, unknown> {
  return {
    ...PRODUCT_PROPS,
    URL: window.location.href,
    'Page Path': window.location.pathname,
    ...localeProps(),
    utm_campaign: utm.campaign ?? '',
    utm_source: utm.source ?? '',
    utm_medium: utm.medium ?? '',
  }
}

let denied = analyticsConsent() === 'denied'
let loadStarted = false

/** Withdrawal is "forget me": wipe every identifier PostHog keeps. The SDK
 * cookie sits on the registrable domain (cross-subdomain default), so it is
 * expired with and without the parent-domain attribute. */
function purgeAnalyticsStorage() {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('ph_') || key.startsWith('__ph')) localStorage.removeItem(key)
    }
    sessionStorage.removeItem(UTM_STORAGE_KEY)
  } catch {
    // storage unavailable — nothing was persisted to begin with
  }
  try {
    const parent = window.location.hostname.split('.').slice(-2).join('.')
    for (const part of document.cookie.split(';')) {
      const name = part.split('=')[0].trim()
      if (!name.startsWith('ph_') && !name.startsWith('__ph')) continue
      document.cookie = `${name}=; max-age=0; path=/`
      document.cookie = `${name}=; max-age=0; path=/; domain=.${parent}`
    }
  } catch {
    // cookie access blocked — ignore
  }
}

if (apiKey) {
  const load = () => {
    if (loadStarted || denied) return
    loadStarted = true
    void import('posthog-js').then(({ default: posthog }) => {
      if (denied) return // withdrawn while the chunk was downloading
      // A visitor who already consented on a static landing page got a
      // `static-…` id from the SDK-less snippet there. Hand it to the SDK so
      // the landing pageview and everything in the app are ONE person — but
      // only while the SDK has no record of its own: a returning visitor's
      // existing id must never be overwritten.
      const staticDid = (() => {
        try {
          if (localStorage.getItem(`ph_${apiKey}_posthog`)) return null
          return localStorage.getItem('stt_static_did')
        } catch {
          return null
        }
      })()
      posthog.init(apiKey, {
        api_host: apiHost || 'https://us.i.posthog.com',
        ...(staticDid ? { bootstrap: { distinctID: staticDid, isIdentifiedID: false } } : {}),
        // Web vitals were switched on by REMOTE config, which
        // advanced_disable_decide below no longer fetches — enable them here
        // or $web_vitals stops arriving and mobile INP becomes unmeasurable
        capture_performance: {
          web_vitals: true,
          web_vitals_allowed_metrics: ['LCP', 'INP', 'CLS', 'FCP'],
        },
        // Captured manually below, AFTER the super properties are registered —
        // otherwise the very first $pageview would miss them
        ui_host: 'https://eu.posthog.com', // the api_host is a reverse proxy; toolbar/UI live here
        capture_pageview: false,
        capture_pageleave: true,
        autocapture: false,
        disable_surveys: true,
        // We don't use feature flags — skip the remote /flags call (it's
        // also CORS-blocked from dev origins and spams the console)
        advanced_disable_decide: true,
        advanced_disable_feature_flags: true,
      })
      // A previous decline may have persisted an opt-out flag — consent is
      // granted now, so clear it (silently, no $opt_in event)
      if (posthog.has_opted_out_capturing()) {
        posthog.opt_in_capturing({ captureEventName: null })
      }
      // Super properties so PostHog's own events carry the standard set too
      posthog.register({ ...PRODUCT_PROPS, ...localeProps() })
      // First touch, once per person for life ($set_once — later visits never
      // overwrite it): the SEO landing page that brought them (our own tag's
      // utm_campaign, same slug format as the snippet writes) or 'app' for a
      // direct entry. It survives identify(), so Purchase Success can be
      // grouped by the page that started the journey.
      const landing = utm.source === 'stt_seo' ? utm.campaign : undefined
      posthog.setPersonProperties(undefined, {
        initial_landing_page: landing || 'app',
        initial_landing_referrer: document.referrer || '$direct',
      })
      if (utm.source || utm.medium || utm.campaign) {
        try {
          sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm))
        } catch {
          // fine — attribution just won't survive a reload
        }
        posthog.register_for_session({
          utm_campaign: utm.campaign ?? '',
          utm_source: utm.source ?? '',
          utm_medium: utm.medium ?? '',
        })
        // Standard User Properties — attribution on the person as well
        posthog.setPersonProperties({
          ...(utm.campaign ? { utm_campaign: utm.campaign } : {}),
          ...(utm.source ? { utm_source: utm.source } : {}),
          ...(utm.medium ? { utm_medium: utm.medium } : {}),
        })
      }
      posthog.capture('$pageview')
      client = posthog
      for (const fn of queue) fn(posthog)
      queue = []
      // INP attribution: $web_vitals only carries the number. The attribution
      // build names the element and splits the delay into input / processing /
      // presentation — the "what exactly is slow on phones" question answers
      // itself from a Target breakdown instead of guesses. ~2 KB, same lazy
      // chunk, consent-gated like everything here.
      void import('web-vitals/attribution').then(({ onINP }) => {
        onINP(
          ({ value, rating, attribution }) => {
            capture('Web Vital INP', {
              Value: Math.round(value),
              Rating: rating,
              Target: attribution.interactionTarget,
              'Interaction Type': attribution.interactionType,
              'Input Delay': Math.round(attribution.inputDelay),
              'Processing Duration': Math.round(attribution.processingDuration),
              'Presentation Delay': Math.round(attribution.presentationDelay),
              'Load State': attribution.loadState,
            })
          },
          { reportAllChanges: false },
        )
      })
    })
  }

  // Consent granted (here or on the main site) → load as before, off the
  // critical path. Undecided → wait for the banner. Denied → never load,
  // and sweep identifiers a past consent may have left behind (e.g. the
  // user revoked on the main site since their last visit).
  if (analyticsConsent() === 'granted') {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => load(), { timeout: 3000 })
    } else {
      setTimeout(load, 1500)
    }
  } else if (denied) {
    purgeAnalyticsStorage()
  }

  onConsentChange((granted) => {
    denied = !granted
    if (granted) {
      // The user just clicked Accept — no idle wait needed
      loadStarted = false
      if (client) {
        client.opt_in_capturing({ captureEventName: null })
      } else {
        load()
      }
    } else {
      queue = []
      client?.opt_out_capturing()
      purgeAnalyticsStorage()
    }
  })
}

function withClient(fn: (p: PostHog) => void) {
  if (!apiKey || denied) return
  if (client) {
    fn(client)
  } else {
    queue.push(fn)
  }
}

function capture(event: string, properties?: Record<string, unknown>) {
  const props = { ...standardProps(), ...properties }
  withClient((p) => p.capture(event, props))
}

/** {Language First, Language First Code} — the audio/source language. */
function languageFirst(sttCode: string) {
  return {
    'Language First': sttEnglishName(sttCode),
    'Language First Code': sttShortCode(sttCode),
  }
}

/** {Language Second/Pair …} — target language plus the pair, per convention. */
function languagePair(sourceSttCode: string | undefined, targetSttCode: string) {
  const second = {
    'Language Second': sttEnglishName(targetSttCode),
    'Language Second Code': sttShortCode(targetSttCode),
  }
  if (!sourceSttCode) return second
  return {
    ...languageFirst(sourceSttCode),
    ...second,
    'Language Pair': `${sttEnglishName(sourceSttCode)}-${sttEnglishName(targetSttCode)}`,
    'Language Pair Code': `${sttShortCode(sourceSttCode)}-${sttShortCode(targetSttCode)}`,
  }
}

export interface CreditProps {
  maxLimit: number
  limitRemaining: number
  subscriptionType: string
}

function creditProps(props: CreditProps) {
  return {
    'Max Limit': props.maxLimit,
    'Limit Remaining': props.limitRemaining,
    'Subscription Type': props.subscriptionType,
  }
}

export function trackPageViewed() {
  capture('Audio Transcription Page Viewed')
}

export function trackAudioSent(props: {
  language: string
  format: string
  fileExtension: string
  fileSize: number
  durationSeconds: number | null
  isSubscribed: boolean
  credits: CreditProps
}) {
  capture('Audio Sent for Transcription', {
    ...languageFirst(props.language),
    'Format Transcription': props.format,
    Duration: props.durationSeconds ?? '',
    'File extension': props.fileExtension,
    'File Size': props.fileSize,
    'Is Subscribed': props.isSubscribed,
    ...creditProps(props.credits),
  })
}

export function trackAudioTranscribed() {
  capture('Audio Transcribed')
}

export function trackLimitExceeded(credits: CreditProps) {
  capture('Transcription Limit Exceeded', creditProps(credits))
}

export function trackLogin() {
  capture('Log In Button Clicked')
}

export function trackLogout() {
  capture('Log Out Button Clicked')
}

export function trackSubscribe(planName: string, authenticated?: boolean) {
  capture('Subscribe Button Clicked', {
    Plan: planName,
    'Subscription Type': planName.toLowerCase(),
    // Anonymous clicks go through sign-in first — the property splits the
    // funnel into "opened checkout" vs "was sent to Auth0"
    ...(authenticated === undefined ? {} : { Authenticated: authenticated }),
  })
}

/** A visitor pressed Subscribe before signing in and is now back from
 * Auth0 — the checkout opens automatically. */
/** The free tier's own button. Without it every registration looks alike in
 * the funnel — the one from the pricing band, the one from the limit modal,
 * and the one that was really the first half of a purchase — and the whole
 * question the band exists to answer ("how many people sign up from the
 * shop window rather than from the dead end?") has no data behind it. */
export function trackFreePlanClicked() {
  capture('Free Plan Button Clicked', {
    Plan: 'Free',
    'Subscription Type': 'free',
  })
}

export function trackCheckoutResumed(planName: string) {
  capture('Checkout Resumed After Login', {
    Plan: planName,
    'Subscription Type': planName.toLowerCase(),
  })
}

export function trackUnsubscribe() {
  capture('Unsubscribe Button Clicked')
}

export function trackCopy() {
  capture('Copy Button Clicked')
}

export function trackDownload(format?: string, targetLangCode?: string) {
  capture('Download Button Clicked', {
    ...(format ? { Format: format } : {}),
    ...(targetLangCode ? { 'Language Second Code': sttShortCode(targetLangCode) } : {}),
  })
}

export function trackDownloadAll() {
  capture('Download All Formats Clicked')
}

/** distinct_id must be the Auth0 `sub` — stable and non-PII (this service
 * sells privacy; the email lives in person properties instead). Must match
 * the server-side webhook capture, which uses auth0_sub too. */
export function identifyUser(auth0Sub: string, props?: Record<string, unknown>) {
  withClient((p) => p.identify(auth0Sub, props))
}

/** Person props that have to reflect reality — an earlier version pinned
 * is_subscribed: false forever, making the paying cohort unqueryable. */
export function setSubscriptionProps(isSubscribed: boolean, planName?: string | null) {
  withClient((p) =>
    p.setPersonProperties({
      is_subscribed: isSubscribed,
      'Subscription Type': isSubscribed && planName ? planName.toLowerCase() : 'free',
    }),
  )
}

/** Route changes inside the SPA — the init-time $pageview covers only the
 * landing; without this /history and friends never appear in funnels. */
export function trackSpaPageview() {
  withClient((p) => p.capture('$pageview'))
}

export function trackCheckoutOpened(planName: string, mode: 'new-tab' | 'same-tab' = 'new-tab') {
  capture('Checkout Opened', {
    Mode: mode,
    Plan: planName,
    'Subscription Type': planName.toLowerCase(),
  })
}

export function trackCheckoutFailed(planName: string, message: string) {
  capture('Checkout Failed', { Plan: planName, Message: message.slice(0, 200) })
}

/** The /auth/me poll saw the subscription flip after checkout — the closest
 * client-side signal to a completed payment (the authoritative Purchase
 * Success event comes from the Lemon webhook, server-side). */
export function trackCheckoutCompleted(planName?: string | null) {
  capture(
    'Checkout Completed',
    planName ? { Plan: planName, 'Subscription Type': planName.toLowerCase() } : {},
  )
}

export function resetAnalytics() {
  withClient((p) => p.reset())
}

export function trackTranslated(props: { source?: string; target: string }) {
  capture('result_translated', {
    ...languagePair(props.source || undefined, props.target),
    // kept for continuity with existing dashboards
    target_lang: props.target,
  })
}

export function trackLanguageAutoDetected(sttCode: string) {
  capture('Language Auto Detected', {
    ...languageFirst(sttCode),
    Detected: sttShortCode(sttCode),
  })
}

export function trackLanguageMismatch(props: {
  detectedName: string
  detectedCode: string
  selectedSttCode: string
  action: 'switched' | 'kept' | 'dismissed'
}) {
  capture('Language Mismatch Warning', {
    Detected: props.detectedName,
    'Detected Code': props.detectedCode,
    Selected: sttEnglishName(props.selectedSttCode),
    'Selected Code': sttShortCode(props.selectedSttCode),
    Action: props.action,
  })
}

/** Locale for the standard props + super properties (Locale Code/Name). */
export function setAnalyticsUiLanguage(code: string) {
  uiLanguage = code
  withClient((p) =>
    p.register({
      'Locale Code': code,
      'Locale Name': UI_LOCALES.find((l) => l.code === code)?.english ?? 'English',
    }),
  )
}

export function trackUiLanguageChanged(from: string, to: string) {
  capture('Interface Language Changed', { From: from, To: to })
}

export function trackTranscriptionFailed(message: string) {
  capture('Transcription Failed', { Message: message.slice(0, 200) })
}

export function trackMicRecordingUsed(seconds: number) {
  capture('Microphone Recording Used', { Duration: seconds })
}

export function trackVideoProcessed(extension: string, fileSize: number) {
  capture('Video File Processed', { 'File extension': extension, 'File Size': fileSize })
}

/** The subtitle converter: no credit is spent, so this event is the only
 * trace of it — and the only way to tell whether the tool brings people who
 * later transcribe something. */
export function trackSubtitleConverted(from: string, to: string, cues: number) {
  capture('Subtitle Converted', { From: from.toUpperCase(), To: to.toUpperCase(), Lines: cues })
}

export function trackCueEdited(view: 'original' | 'translated') {
  capture('Transcript Line Edited', { View: view })
}

/** The subtitle-segmentation switch was flipped. Whether the default should
 * change is a question this event answers in a month. */
export function trackSubtitleSegmentation(on: boolean, where: 'result' | 'history') {
  capture('Subtitle Segmentation Toggled', { Enabled: on, Where: where })
}

/** A line was dropped from the result — usually a phrase the engine repeated. */
export function trackCueDeleted() {
  capture('Transcript Line Deleted')
}

export function trackDuplicateUpload(action: 'open_history' | 'transcribe_again' | 'dismissed') {
  capture('Duplicate Upload Detected', { Action: action })
}
